import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Nav, Image, Button, Accordion } from "@themesberg/react-bootstrap";
import SimpleBar from "simplebar-react";
import { CSSTransition } from "react-transition-group";
import {
  Building2,
  PanelRightClose,
  PanelLeftClose,
  ChevronDown,
  LayoutDashboard,
  Package,
  ScanBarcode,
  Settings,
  List,
  ClipboardList,
  FileLock2,
  ChevronUp,
  Tag,
} from "lucide-react";
import SiteLogo from "../assets/img/site-logo.png";
import { Routes } from "../routes";

export default ({ showMobile, toggleMobile, collapsed, toggleCollapse }) => {
  const location = useLocation();
  const { pathname, state } = location;

  const [sectionState, setSectionState] = useState(
    state?.section || localStorage.getItem("selectedModule") || "Admin"
  );

  // ✅ Active sections now handled as an array
  const [activeKeys, setActiveKeys] = useState(() => {
    const saved = localStorage.getItem("sidebarActiveKeys");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("sidebarActiveKeys", JSON.stringify(activeKeys));
  }, [activeKeys]);

  useEffect(() => {
    if (state?.section && state.section !== sectionState) {
      setSectionState(state.section);
      localStorage.setItem("selectedModule", state.section);
    }
  }, [state?.section]);

  useEffect(() => {
    const handler = (e) => {
      const newModule =
        e?.detail || localStorage.getItem("selectedModule") || "Admin";
      setSectionState(newModule);
    };
    window.addEventListener("selectedModuleChanged", handler);
    return () => window.removeEventListener("selectedModuleChanged", handler);
  }, []);
  useEffect(() => {
  const normalized = normalizePath(pathname);

  if (salesPaths.some(path => normalized.startsWith(path))) {
    setSectionState("Sales");
    localStorage.setItem("selectedModule", "Sales");
  }
}, [pathname]);
useEffect(() => {
  const normalized = normalizePath(pathname);

  if (
    normalized.startsWith(Routes.ListingPricing.path) ||
    normalized.startsWith(Routes.TradeInListing.path)
  ) {
    setActiveKeys(["Listing"]);
  } else if (normalized.startsWith(Routes.TradeInPricing.path) || normalized.startsWith("/platform-pricing")) {
    setActiveKeys(["Pricing"]);
  }
}, [pathname]);


  const normalizePath = (path) => path?.replace(/\/+$/, "") || "";
  const salesPaths = [
  Routes.ListingPricing.path,      // "/listing-pricing"
  Routes.TradeInListing.path,      // "/trade-in-listing"
  Routes.TradeInPricing.path,      // "/trade-in-pricing"
  "/platform-order",
  "platform-order-shopee",
  "platform-order-lazada",
  "platform-order-tiktok",
  "/platform-pricing",
].map(normalizePath);

  const companyRelatedPaths = [
    Routes.Company.path,
    Routes.EditOrganisation.path,
    Routes.AddEntities.path,
    Routes.AddWorkPlaces.path,
  ].map(normalizePath);

  const currentPath = normalizePath(
    pathname === "/platform-manage-order" && state?.fromPath
      ? state.fromPath
      : pathname
  );
  // ✅ Close all dropdowns when section/module changes
useEffect(() => {
  const handleModuleChange = (e) => {
    const newSection = e?.detail || localStorage.getItem("selectedModule");

    // Only reset when switching between modules (Admin, Inventory, Sales)
    if (newSection && newSection !== sectionState) {
      setActiveKeys([]); // close all open accordions
      localStorage.removeItem("sidebarActiveKeys");
    }
  };

  window.addEventListener("selectedModuleChanged", handleModuleChange);

  return () => window.removeEventListener("selectedModuleChanged", handleModuleChange);
}, [sectionState]);


  // ✅ Modified CollapsableNavItem — supports multiple open sections
  const CollapsableNavItem = ({ eventKey, title, icon, children }) => {
    const isOpen = activeKeys.includes(eventKey);

    const hasActiveChild = React.Children.toArray(children).some((child) => {
      if (!React.isValidElement(child)) return false;
      const childLink = normalizePath(child.props.link);

      if (child.props.title === "Company") {
        return companyRelatedPaths.some(
          (path) => currentPath === path || currentPath.startsWith(path + "/")
        );
      }

      return (
        childLink &&
        (currentPath === childLink || currentPath.startsWith(childLink + "/"))
      );
    });

    const handleAccordionClick = () => {
      if (collapsed) {
        toggleCollapse(false);
        return;
      }

      // ✅ Toggle multiple sections open/close independently
      setActiveKeys((prev) =>
        prev.includes(eventKey)
          ? prev.filter((key) => key !== eventKey)
          : [...prev, eventKey]
      );
    };

    return (
      <Accordion
        as={Nav.Item}
        activeKey={isOpen ? eventKey : null}
        className={`mm-collapsable ${isOpen || hasActiveChild ? "mm-open" : ""}`}
      >
        <Accordion.Item eventKey={eventKey}>
          <Accordion.Button
            as="div"
            role="button"
            className={`d-flex align-items-center justify-content-between nav-link ${
              collapsed && hasActiveChild ? "active" : ""
            }`}
            onClick={handleAccordionClick}
          >
            <div className="d-flex align-items-center">
              <span className="me-2">{icon}</span>
              {(!collapsed || showMobile) && <span>{title}</span>}
            </div>

            {(!collapsed || showMobile) &&
              (isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />)}
          </Accordion.Button>

          {(!collapsed || showMobile) && isOpen && (
            <Accordion.Body>
              <Nav className="flex-column">{children}</Nav>
            </Accordion.Body>
          )}
        </Accordion.Item>
      </Accordion>
    );
  };

  const NavItem = ({ title, link, icon, extraActivePaths = [] }) => {
    const linkNormalized = normalizePath(link);
    const isActive =
      currentPath === linkNormalized ||
      currentPath.startsWith(linkNormalized + "/") ||
      extraActivePaths.some(
        (path) =>
          currentPath === normalizePath(path) ||
          currentPath.startsWith(normalizePath(path) + "/")
      );

    return (
      <Nav.Item className={isActive ? "active" : ""}>
        <Nav.Link
          as={Link}
          to={{ pathname: link || "#", state: { section: sectionState } }}
          className="d-flex align-items-center"
        >
          <span className="me-2">{icon}</span>
          {(!collapsed || showMobile) && <span>{title}</span>}
        </Nav.Link>
      </Nav.Item>
    );
  };

  const handleToggle = () => {
    if (window.innerWidth <= 767) {
      toggleMobile();
    } else {
      toggleCollapse();
    }
  };

  // Navigation sections unchanged
  const navItems =
    sectionState === "Admin" ? (
      <>
        <NavItem
          title="Dashboard"
          link={Routes.DashboardOverview.path}
          icon={<LayoutDashboard size={24} />}
        />
        <CollapsableNavItem
          eventKey="organisation"
          title="Organisation"
          icon={<Building2 size={24} />}
        >
          <NavItem title="Company" link={Routes.Company.path} />
          <NavItem title="Department" link={Routes.Department.path} />
          <NavItem title="Employee" link={Routes.Employee.path} />
        </CollapsableNavItem>
      </>
    ) : sectionState === "Inventory" ? (
      <>
        <NavItem
          title="Dashboard"
          link={Routes.DashboardOverview.path}
          icon={<LayoutDashboard size={24} />}
        />
        <CollapsableNavItem
          eventKey="Product"
          title="Product"
          icon={<Package size={24} />}
        >
          <NavItem title="Brand" link={Routes.Brand.path} />
          <NavItem title="Category" link={Routes.Category.path} />
          <NavItem title="SPU & SKU" link={Routes.Spusku.path} extraActivePaths={[
    "/inventory/spus" // 👈 this covers /inventory/spus/:id/skus
  ]} />
        </CollapsableNavItem>
        <CollapsableNavItem
          eventKey="Stock"
          title="Stock"
          icon={<ScanBarcode size={24} />}
        >
          <NavItem title="Stock List" link={Routes.StockList.path}  extraActivePaths={[ "/inventory/stock" ]}/>
        </CollapsableNavItem>
        <CollapsableNavItem
          eventKey="Settings"
          title="Settings"
          icon={<Settings size={24} />}
        >
          <NavItem title="SKU Preset" link={Routes.SkuPresets.path} />
          <NavItem title="SKU Code Scheme" link={Routes.SkuScheme.path} />
          <NavItem title="Branch" link={Routes.Branch.path} />
        </CollapsableNavItem>
      </>
    ) : sectionState === "Sales" ? (
      <>
        {/* <NavItem
          title="Dashboard"
          link={Routes.DashboardOverview.path}
          icon={<LayoutDashboard size={24} />}
        /> */}
        <CollapsableNavItem
          eventKey="Listing"
          title="Listing"
          icon={<List size={24} />}
        >
          <NavItem title="Platform Listing" link="/listing-pricing" />
          {/* <NavItem title="Trade-In Listing" link="/trade-in-listing" /> */}
        </CollapsableNavItem>
        
        <CollapsableNavItem
          eventKey="Pricing"
          title="Pricing"
          icon={<Tag size={24} />}
        >
          <NavItem title="Platform Pricing" link="/platform-pricing" />
          {/* <NavItem title="Trade-In Pricing" link={Routes.TradeInPricing.path} extraActivePaths={[Routes.TradeInPricingPromotional.path, Routes.TradeInPricingHistory.path]} />  */}
        </CollapsableNavItem>
         
        <CollapsableNavItem
          eventKey="Order"
          title="Order - Platform"
          icon={<ClipboardList size={24} />}
        >
          <NavItem title="Shopee SG" link="/platform-order-shopee" />
          <NavItem title="Lazada SG" link="/platform-order-lazada" />
          <NavItem title="TikTok SG" link="/platform-order-tiktok" />
        </CollapsableNavItem>
         

        {/* <CollapsableNavItem
          eventKey="Order"
          title="Order"
          icon={<ClipboardList size={24} />}
        >
          <NavItem title="Platform Order" link="/platform-order" />
        </CollapsableNavItem> */}
        <CollapsableNavItem
          eventKey="Permission"
          title="Permission"
          icon={<FileLock2 size={24} />}
        >
          <NavItem title="Permission Group" link="/organisation" />
        </CollapsableNavItem>
      </>
    ) : null;

  return (
    <>
      <CSSTransition
        timeout={300}
        in={showMobile || !collapsed}
        classNames="sidebar-transition"
        appear
      >
        <SimpleBar
          className={[
            "sidebar text-white mm-sidebar",
            showMobile ? "show-mobile" : "hide-mobile",
            !showMobile && collapsed ? "collapsed-sidebar" : "",
          ].join(" ")}
        >
          <div className="sidebar-inner p-2">
            <div
              className="d-flex justify-content-between align-items-center mb-2 px-2 pt-2 pb-4 brand-wrapper"
              style={{ borderBottom: "1px solid #CED4DA" }}
            >
              {(!collapsed || showMobile) ? (
                <Link
                  to={{
                    pathname: Routes.DashboardOverview.path,
                    state: { section: sectionState },
                  }}
                >
                  <Image src={SiteLogo} height={32} />
                </Link>
              ) : (
                <Button variant="link" className="p-0 m-0 border-0" disabled />
              )}
              <Button
                variant="light"
                onClick={handleToggle}
                className="toggle-btn"
              >
                {collapsed ? <PanelRightClose /> : <PanelLeftClose />}
              </Button>
            </div>

            <Nav className="flex-column">{navItems}</Nav>
          </div>
        </SimpleBar>
      </CSSTransition>
    </>
  );
};